import { BarChart3, FileText, Receipt, TrendingUp } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import type { IStoreIncomingMasterList } from '../../api/IncomingInvoice.interface';

import FilterBox from './FilterBox';
import InvoicesIncomingTable from './InvoicesTable';

interface IncomeInvoicesListProps {
  startDate: string;
  endDate: string;
  setStartDate: React.Dispatch<React.SetStateAction<string>>;
  setEndDate: React.Dispatch<React.SetStateAction<string>>;
  invoicesList: IStoreIncomingMasterList[] | undefined;
  isLoading: boolean;
  invoiceId: number | null;
  setInvoiceId: React.Dispatch<React.SetStateAction<number | null>>;
  resetFilters: () => void;
  selectedInvoice: number | null;
  setSelectedInvoice: (invoiceId: number) => void;
}

function IncomeInvoicesList({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  invoicesList,
  isLoading,
  invoiceId,
  setInvoiceId,
  resetFilters,
  selectedInvoice,
  setSelectedInvoice,
}: IncomeInvoicesListProps) {
  // Calculate statistics
  const totalInvoices = invoicesList?.length || 0;
  const recentInvoices =
    invoicesList?.filter((invoice) => {
      const invoiceDate = new Date(invoice.TrDate);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return invoiceDate >= weekAgo;
    })?.length || 0;
  const highValueInvoices =
    invoicesList?.filter((invoice) => invoice.TotalInvoice > 1000)?.length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800">
      {/* Header Section */}
      <div className="mb-8">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-700 p-6 shadow-xl">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-30">
            <svg
              width="60"
              height="60"
              viewBox="0 0 60 60"
              xmlns="http://www.w3.org/2000/svg"
              className="h-full w-full"
            >
              <g fill="none" fillRule="evenodd">
                <g fill="rgba(255,255,255,0.1)">
                  <circle cx="30" cy="30" r="2" />
                </g>
              </g>
            </svg>
          </div>

          {/* Floating Elements */}
          <div className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
            <Receipt className="h-4 w-4 text-white" />
          </div>
          <div className="absolute -bottom-3 -left-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
            <BarChart3 className="h-4 w-4 text-white" />
          </div>

          {/* Main Content */}
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 shadow-lg backdrop-blur-sm">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">فواتير الشراء</h1>
                <p className="text-base text-emerald-100">
                  إدارة وتتبع جميع فواتير الشراء الواردة
                </p>
                {/* Stage Indicator */}
                <div className="mt-2 flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="border-white/30 bg-white/10 text-white backdrop-blur-sm"
                  >
                    المرحلة الأولى: البحث والاختيار
                  </Badge>
                </div>
              </div>
            </div>

            <div className="hidden items-center gap-3 md:flex">
              <Badge
                variant="outline"
                className="border-white/30 bg-white/10 text-white backdrop-blur-sm"
              >
                <TrendingUp className="mr-2 h-4 w-4" />
                {totalInvoices} فاتورة
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card className="border-0 bg-gradient-to-br from-blue-50 to-indigo-100 shadow-lg backdrop-blur-sm dark:from-blue-950/50 dark:to-indigo-900/50">
          <CardContent className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  إجمالي الفواتير
                </p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {totalInvoices}
                </p>
              </div>
            </div>
            <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30"></div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-gradient-to-br from-amber-50 to-orange-100 shadow-lg backdrop-blur-sm dark:from-amber-950/50 dark:to-orange-900/50">
          <CardContent className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  الأسبوع الماضي
                </p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {recentInvoices}
                </p>
              </div>
            </div>
            <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900/30"></div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-gradient-to-br from-emerald-50 to-teal-100 shadow-lg backdrop-blur-sm dark:from-emerald-950/50 dark:to-teal-900/50">
          <CardContent className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
                <Receipt className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  عالية القيمة
                </p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {highValueInvoices}
                </p>
              </div>
            </div>
            <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30"></div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Area - Two Column Grid Layout */}
      <div className="grid grid-cols-1 gap-8 xl:grid-cols-[400px_1fr]">
        {/* Left Column - Filters and Other Data */}
        <div className="space-y-6">
          {/* Filter Section */}
          <Card className="border-0 bg-gradient-to-br from-white/80 to-slate-50/80 shadow-lg backdrop-blur-sm dark:from-slate-900/80 dark:to-slate-800/80">
            <CardHeader className="border-b border-slate-200/50 bg-slate-50/30 px-6 py-4 dark:border-slate-700/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 shadow-md">
                  <BarChart3 className="h-4 w-4 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                    فلاتر البحث
                  </CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    حدد معايير البحث لعرض الفواتير المطلوبة
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <FilterBox
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                isLoading={isLoading}
                invoiceId={invoiceId}
                setInvoiceId={setInvoiceId}
                resetFilters={resetFilters}
              />
            </CardContent>
          </Card>

          {/* Additional Data Section */}
          <Card className="border-0 bg-gradient-to-br from-white/80 to-slate-50/80 shadow-lg backdrop-blur-sm dark:from-slate-900/80 dark:to-slate-800/80">
            <CardHeader className="border-b border-slate-200/50 bg-slate-50/30 px-6 py-4 dark:border-slate-700/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md">
                  <TrendingUp className="h-4 w-4 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                    معلومات إضافية
                  </CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    إحصائيات وتحليلات الفواتير
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-950/30">
                    <div className="text-center">
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        إجمالي القيمة
                      </p>
                      <p className="text-lg font-bold text-blue-900 dark:text-blue-100">
                        {(invoicesList || [])
                          .reduce(
                            (sum, invoice) => sum + invoice.TotalInvoice,
                            0,
                          )
                          .toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 p-3 dark:bg-emerald-950/30">
                    <div className="text-center">
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">
                        متوسط القيمة
                      </p>
                      <p className="text-lg font-bold text-emerald-900 dark:text-emerald-100">
                        {totalInvoices > 0
                          ? (
                              (invoicesList || []).reduce(
                                (sum, invoice) => sum + invoice.TotalInvoice,
                                0,
                              ) / totalInvoices
                            ).toLocaleString()
                          : 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status Distribution */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    توزيع الفواتير
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-600 dark:text-slate-400">
                        فواتير حديثة
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {recentInvoices}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-600 dark:text-slate-400">
                        عالية القيمة
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {highValueInvoices}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Table */}
        <div className="space-y-6">
          <Card className="border-0 bg-gradient-to-br from-white/80 to-slate-50/80 shadow-lg backdrop-blur-sm dark:from-slate-900/80 dark:to-slate-800/80">
            <CardHeader className="border-b border-slate-200/50 bg-slate-50/30 px-6 py-4 dark:border-slate-700/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md">
                  <FileText className="h-4 w-4 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                    قائمة الفواتير
                  </CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    اختر فاتورة لعرض تفاصيلها
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <InvoicesIncomingTable
                resetFilters={resetFilters}
                items={invoicesList || []}
                isLoading={isLoading}
                selectedInvoice={selectedInvoice}
                setSelectedInvoice={setSelectedInvoice}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer Decoration */}
      <div className="mt-12 flex justify-center">
        <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
          <div className="h-1 w-8 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600"></div>
          <span>نظام إدارة فواتير الشراء</span>
          <div className="h-1 w-8 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600"></div>
        </div>
      </div>
    </div>
  );
}

export default IncomeInvoicesList;
